import {
  Directive,
  HostListener,
  Inject,
  ElementRef,
  Renderer2,
  Input
} from '@angular/core';
import { ValueAccessorProviderFactory } from '../value-accessor/value-accessor-provider-factory';
import { PhonePipe } from '../../pipes/phone.pipe';
import { BaseNumberInputDirective } from './base-number-input.directive';

import type { OnChanges } from '@angular/core';
import type { ControlValueAccessor } from '@angular/forms';

/**
 * Phone Input Directive
 *
 * Allows an input type number to be formatted on blur
 *
 * @example
 * <input type="number" tdWbPhoneInput>
 */
@Directive({
  selector: 'input[tdWbPhoneInput]',
  providers: [ValueAccessorProviderFactory(PhoneInputDirective)]
})
export class PhoneInputDirective extends BaseNumberInputDirective implements ControlValueAccessor, OnChanges {
  private readonly DEFAULT_LENGTH: number = 10;
  private readonly COUNTRY_CODE_LENGTH: number = 11;
  @Input('tdWbPhoneInput')
  set countryCode(value: boolean) { this._countryCode = value;}

  get countryCode(): boolean { return this._countryCode; }

  private _countryCode: boolean = false;

  constructor(@Inject(ElementRef) protected elementRef: ElementRef,
              @Inject(Renderer2) protected renderer: Renderer2,
              @Inject(PhonePipe) protected phonePipe: PhonePipe) {
    super('true', elementRef, renderer);
  }

  // Prevent additive input beyond max length, but allow deletion/navigation.
  // Block non-digit printable keys (including '.' and ',').
  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const el = this.elementRef.nativeElement as HTMLInputElement;
    const key = event.key;

    // Allow control/meta shortcuts and navigation/deletion keys
    if (event.ctrlKey || event.metaKey || event.altKey) { return; }
    if (
      key === 'Backspace' || key === 'Delete' || key === 'Tab' ||
      key === 'Enter' || key === 'Escape' || key === 'Home' || key === 'End' ||
      key.startsWith('Arrow')
    ) {
      return; // never block these
    }

    // Only handle single-character (printable) keys from here
    if (key.length !== 1) { return; }

    // Phone input accepts digits only; explicitly block '.' and ',' and any other non-digit
    if (!/^[0-9]$/.test(key)) {
      event.preventDefault();
      return;
    }

    const value = el.value ?? '';
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const selected = Math.max(0, end - start);
    const max = this.maxLength();

    // Remaining capacity after replacing current selection
    const remaining = max - (value.length - selected);
    if (remaining <= 0) {
      // At capacity with no selection: block additive input
      event.preventDefault();
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    // Stop data actually being pasted into input
    // Also stopImmediatePropagation to prevent the base directive's paste handler
    // from running on the same element and double-processing the value.
    event.stopImmediatePropagation?.();
    event.stopPropagation();
    event.preventDefault();

    const input = event.target as HTMLInputElement;
    const before = input.value ?? '';
    const start = input.selectionStart ?? before.length;
    const end = input.selectionEnd ?? before.length;

    // Get pasted data via clipboard API, keep digits only
    const digits = (event.clipboardData?.getData('text') ?? '').replace(/[^0-9]+/g, '');

    const max = this.maxLength();
    const capacity = Math.max(0, max - (before.length - (end - start)));
    const insert = digits.slice(0, capacity);

    const next = before.slice(0, start) + insert + before.slice(end);
    this.renderer.setProperty(input, 'value', next);

    // Trigger base processing by simulating a minimal KeyboardEvent shape
    const fakeEvent = { target: input, key: '' } as unknown as KeyboardEvent;
    super.onKeyUp(fakeEvent);
  }

  ngOnChanges(): void {
    super.onChanges();
  }

  writeValue(value: unknown): void {
    let formattedValue = '';

    if (!this.isEmpty(value)) {
      if (!this.focused) {
        formattedValue = this.formatValue(value as number);
      } else {
        formattedValue = value.toString();
      }
    }
    if ((this.viewValue !== formattedValue || this.viewValue === undefined)) {
      this.renderer.setProperty(this.elementRef.nativeElement, 'value', formattedValue);
      this.viewValue = formattedValue;
    }
    this.value = value as number;
  }

  protected formatValue(value: number): string {
    return this.phonePipe.transform(value?.toString());
  }

  protected parseValue(value: string): number {
    return Number(value);
  }

  private maxLength(): number {
    return this.countryCode ? this.COUNTRY_CODE_LENGTH : this.DEFAULT_LENGTH;
  }
}
